// src/lib/navigation.ts
// Returns 'forward' | 'back' | 'tab' for the current navigation. Drives
// AnimatePresence variants in App.tsx.
//
// - 'tab': previous and current paths are both in TAB_PATHS (or descendants)
//   AND map to different tab indices → fade
// - 'back': react-router reports POP (browser back, navigate(-1)) → slide right
// - 'forward': anything else (PUSH / REPLACE) → slide left
import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { destinationFor } from '@/lib/appNavigation';


export type NavigationDirection = 'forward' | 'back' | 'tab';

export function useNavigationDirection(): NavigationDirection {
  const location = useLocation();
  const navType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'
  const prevPath = useRef(location.pathname);

  const prevTab = destinationFor(prevPath.current);
  const newTab = destinationFor(location.pathname);

  let direction: NavigationDirection;
  if (prevTab && newTab && prevTab !== newTab) {
    direction = 'tab';
  } else if (navType === 'POP') {
    direction = 'back';
  } else {
    direction = 'forward';
  }

  useEffect(() => {
    prevPath.current = location.pathname;
  }, [location.pathname]);

  return direction;
}
